import type { SyncPullResponse, SyncPushRequest, SyncPushResponse } from "@shared/contracts";
import { PosLocalRepository } from "./pos-repository";
import { DEFAULT_SYNC_RETRY_POLICY, isRetryableSyncStatus, withSyncRetry, type SyncRetryPolicy } from "./sync-retry";

export type SyncResult = {
  pushed: number;
  pulled: number;
  cursor: number;
  hasMore?: boolean;
  configChanged?: boolean;
  configRevision?: number | null;
  configError?: string;
};

export type SyncIdentity = Pick<SyncPushRequest, "clientId" | "storeId" | "actorId" | "deviceId">;

export type SyncClientOptions = {
  maxPullPages?: number;
  pullFrom?: string;
};

export class SyncClient {
  constructor(
    private readonly repository: PosLocalRepository,
    private readonly identity: SyncIdentity,
    private readonly fetcher: typeof fetch = fetch,
    private readonly retryPolicy: SyncRetryPolicy = DEFAULT_SYNC_RETRY_POLICY,
    private readonly sleep: (delayMs: number) => Promise<void> = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs)),
    private readonly options: SyncClientOptions = {},
  ) {}

  async sync(): Promise<SyncResult> {
    const pending = await this.repository.listPendingMutations();
    let pushed = 0;
    if (pending.length > 0) {
      const pushCorrelationId = crypto.randomUUID();
      let previousPushActionId: string | undefined;
      const pushResponse = await this.request<SyncPushResponse>("/api/sync/push", undefined, () => {
        const actionId = crypto.randomUUID();
        const retryOfActionId = previousPushActionId;
        previousPushActionId = actionId;
        return {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            ...this.identity,
            mutations: pending,
            sync: {
              clientStartedAt: new Date().toISOString(),
              correlationId: pushCorrelationId,
              actionId,
              ...(retryOfActionId ? { retryOfActionId } : {}),
            },
          }),
        };
      });
      const acknowledged = [...pushResponse.acceptedMutationIds, ...pushResponse.alreadyAppliedMutationIds];
      await this.repository.applyOrderNumberAssignments(pushResponse.orderNumberAssignments ?? []);
      await this.repository.acknowledgeMutations(acknowledged);
      pushed = acknowledged.length;
    }

    let after = await this.repository.getSyncCursor();
    let pulled = 0;
    let pullPages = 0;
    const maxPullPages = Math.max(1, Math.floor(this.options.maxPullPages ?? 1));
    for (;;) {
      const pullCorrelationId = crypto.randomUUID();
      let previousPullActionId: string | undefined;
      const pullFrom = this.options.pullFrom ? `&from=${encodeURIComponent(this.options.pullFrom)}` : "";
      const pullResponse = await this.request<SyncPullResponse>(`/api/sync/pull?after=${after}&limit=100${pullFrom}`, undefined, () => {
        const actionId = crypto.randomUUID();
        const retryOfActionId = previousPullActionId;
        previousPullActionId = actionId;
        return {
          credentials: "same-origin",
          headers: {
            "x-posnext-client-id": this.identity.clientId,
            "x-posnext-sync-client-started-at": new Date().toISOString(),
            "x-posnext-sync-correlation-id": pullCorrelationId,
            "x-posnext-sync-action-id": actionId,
            ...(retryOfActionId ? { "x-posnext-sync-retry-of-action-id": retryOfActionId } : {}),
          },
        };
      });
      if (!Number.isSafeInteger(pullResponse.cursor) || pullResponse.cursor < after) throw new Error(`Sync cursor regressed (${after} -> ${pullResponse.cursor})`);
      if (pullResponse.sync && (pullResponse.sync.correlationId !== pullCorrelationId || !pullResponse.sync.actionId)) throw new Error("Sync pull trace identity changed");
      const clientReceivedAt = new Date().toISOString();
      try {
        await this.repository.upsertRemoteOrders(pullResponse.orders);
        await this.repository.upsertRemoteEvents(pullResponse.events ?? []);
      } catch (error) {
        if (pullResponse.sync) {
          await this.request("/api/sync/pull/ack", {
            method: "POST",
            credentials: "same-origin",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              ...pullResponse.sync,
              clientReceivedAt,
              clientAppliedAt: new Date().toISOString(),
              outcome: "failed",
              errorCode: "local_apply_failed",
            }),
          }).catch(() => undefined);
        }
        throw error;
      }
      if (pullResponse.sync) {
        const clientAppliedAt = new Date().toISOString();
        await this.request("/api/sync/pull/ack", {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            ...pullResponse.sync,
            clientReceivedAt,
            clientAppliedAt,
            outcome: "success",
          }),
        });
      }
      await this.repository.setSyncCursor(pullResponse.cursor);
      pulled += pullResponse.orders.length;
      if (!pullResponse.hasMore) return { pushed, pulled, cursor: pullResponse.cursor };
      if (pullResponse.cursor === after) throw new Error("Sync recovery made no cursor progress");
      pullPages += 1;
      if (pullPages >= maxPullPages) return { pushed, pulled, cursor: pullResponse.cursor, hasMore: true };
      after = pullResponse.cursor;
    }
  }

  private async request<T>(input: RequestInfo | URL, init?: RequestInit, initFactory?: (attempt: number) => RequestInit): Promise<T> {
    return withSyncRetry(async (attempt) => {
      const requestInit = initFactory ? initFactory(attempt) : init;
      let response: Response;
      try {
        response = await this.fetcher.call(globalThis, input, requestInit);
      } catch (error) {
        const wrapped = new Error(error instanceof Error ? error.message : "Sync request failed");
        Object.assign(wrapped, { retryable: true });
        throw wrapped;
      }
      if (!response.ok) {
        const error = new Error(`Sync request failed (${response.status})`);
        Object.assign(error, { retryable: isRetryableSyncStatus(response.status) });
        throw error;
      }
      return (await response.json()) as T;
    }, (error) => Boolean(error && typeof error === "object" && "retryable" in error && (error as { retryable?: boolean }).retryable), this.retryPolicy, this.sleep);
  }
}
